import type {
  ErrorSchemaRecord,
  InferErrorSchemaOutput,
} from '../types/error-schema.js';
import type {
  AbstractResponse,
  BaseEndpointOptions,
  BuilderContext,
} from '../types/index.js';
import { createHandler } from './create-handler.js';

/**
 * Determines the return type for a stream based on errorSchema and the
 * MINT-OVERRIDE `fullResponse` flag.
 *
 * - When `fullResponse: true`, the success branch (and the discriminated-error
 *   branch, if present) is wrapped in AbstractResponse<...> so consumers can
 *   read status/statusText/headers (e.g. Content-Disposition for filename).
 * - When `fullResponse` is omitted, behavior matches upstream (Blob, or
 *   Blob | error data when errorSchema is set under useDiscriminatorResponse).
 */
type StreamReturnType<Config extends BaseEndpointOptions> =
  // MINT-OVERRIDE: fullResponse branch
  Config extends { fullResponse: true }
    ? Config['errorSchema'] extends ErrorSchemaRecord
      ?
          | AbstractResponse<Blob>
          | AbstractResponse<InferErrorSchemaOutput<Config['errorSchema']>>
      : AbstractResponse<Blob>
    : Config['errorSchema'] extends ErrorSchemaRecord
      ? Blob | InferErrorSchemaOutput<Config['errorSchema']>
      : Blob;

/**
 * Creates a stream handler function for downloading files as Blob.
 *
 * The returned function will:
 * - Validate request data against `requestSchema` (if provided)
 * - Validate query parameters against `querySchema` (if provided)
 * - Make the HTTP request with `responseType: 'blob'`
 * - Return the response as a Blob
 * - Handle errors according to errorSchema if provided
 *
 * @param options - Stream endpoint configuration
 * @param options.method - HTTP method
 * @param options.url - URL pattern with optional parameters (e.g., '/files/$fileId')
 * @param options.requestSchema - Optional Zod schema for validating request body (POST, PUT, PATCH only)
 * @param options.querySchema - Optional Zod schema for validating query parameters
 * @param options.errorSchema - Optional record mapping status codes to Zod schemas for error responses
 * @param context - Builder context containing client getter and configuration
 * @returns A function that makes the HTTP request and returns a Blob or error response
 *
 * @example
 * ```ts
 * const downloadFile = createStream({
 *   method: 'GET',
 *   url: '/files/$fileId',
 *   querySchema: z.object({ format: z.enum(['pdf', 'docx']) }),
 *   errorSchema: {
 *     404: z.object({ error: z.literal('File not found') })
 *   }
 * }, context)
 *
 * const result = await downloadFile({
 *   urlParams: { fileId: '123' },
 *   params: { format: 'pdf' }
 * })
 *
 * if (result instanceof Blob) {
 *   // Success - save file
 * } else {
 *   // Error response
 *   console.error(result.error)
 * }
 * ```
 */
export function createStream<Config extends BaseEndpointOptions>(
  options: Config,
  context: BuilderContext,
) {
  return createHandler<Config, StreamReturnType<Config>>({
    options,
    context,
    errorSchema: options.errorSchema,
    urlParamsSchema: options.urlParamsSchema,
    transformRequest: (request) => ({
      responseType: 'blob',
      ...request,
    }),
    // MINT-OVERRIDE: when fullResponse is set, return the whole AbstractResponse
    // so consumers can read status/statusText/headers. Otherwise behave exactly
    // as upstream — return just the Blob from result.data.
    ...(options.fullResponse
      ? {
          transformFullResponse: (r) =>
            ({ ...r, data: r.data as Blob }) satisfies AbstractResponse<Blob>,
          fullResponse: true,
        }
      : {
          transformResponse: (data) => data as Blob,
        }),
  });
}
